import AxiosInstance from '@/api/axios';
import {
  checkSuperOwner,
  checkOwner,
  checkUserAdmin,
} from '@/utils/checkAccess';
import { getClientId } from '@/utils/helper';
import { useState } from 'react';
import Button from '../Base/Button';
import { Dialog } from '../Base/Headless';
import Lucide from '../Base/Lucide';
import Tippy from '../Base/Tippy';
import Loading from '../Loading/Loading';

interface ITwilioTemplate {
  sid: string;
}

interface TwilioQuickReplyAction {
  id: string;
  title: string;
}

interface TwilioCtaAction {
  id: string;
  title: string;
  type: 'URL' | 'PHONE_NUMBER' | string;
  url?: string;
  phone?: string;
}

interface TwilioTemplateData {
  type: string;
  body?: string;
  media?: string[];
  actions?: TwilioQuickReplyAction[] | TwilioCtaAction[];
}

const Section: React.FC<{
  label: string;
  children: React.ReactNode;
  noPad?: boolean;
}> = ({ label, children, noPad }) => (
  <div className='border-t border-slate-200 dark:border-darkmode-400'>
    <p className='text-[10px] uppercase tracking-widest text-slate-400 px-3.5 pt-2.5 pb-1.5'>
      {label}
    </p>
    <div className={noPad ? '' : 'px-3.5 pb-3'}>{children}</div>
  </div>
);

const QuickReplies: React.FC<{ actions: TwilioQuickReplyAction[] }> = ({
  actions,
}) => (
  <table className='w-full text-xs border-collapse'>
    <thead>
      <tr className='bg-slate-50 dark:bg-darkmode-700'>
        <th className='text-left font-medium text-slate-500 dark:text-slate-400 px-3.5 py-2 w-1/2'>
          Button text
        </th>
        <th className='text-left font-medium text-slate-500 dark:text-slate-400 px-3.5 py-2'>
          Button ID
        </th>
      </tr>
    </thead>
    <tbody>
      {actions.map((action) => (
        <tr
          key={action.id}
          className='border-t border-slate-100 dark:border-darkmode-500'
        >
          <td className='px-3.5 py-2 text-slate-700 dark:text-slate-200'>
            {action.title}
          </td>
          <td className='px-3.5 py-2 font-mono text-[11px] text-slate-400 dark:text-slate-500'>
            {action.id}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const CtaButtons: React.FC<{ actions: TwilioCtaAction[] }> = ({ actions }) => (
  <table className='w-full text-xs border-collapse'>
    <thead>
      <tr className='bg-slate-50 dark:bg-darkmode-700'>
        <th className='text-left font-medium text-slate-500 dark:text-slate-400 px-3.5 py-2'>
          Button text
        </th>
        <th className='text-left font-medium text-slate-500 dark:text-slate-400 px-3.5 py-2'>
          Type
        </th>
        <th className='text-left font-medium text-slate-500 dark:text-slate-400 px-3.5 py-2'>
          Value
        </th>
      </tr>
    </thead>
    <tbody>
      {actions.map((action) => {
        const isPhone = action.type?.toUpperCase() === 'PHONE_NUMBER';
        return (
          <tr
            key={action.id}
            className='border-t border-slate-100 dark:border-darkmode-500'
          >
            <td className='px-3.5 py-2 text-slate-700 dark:text-slate-200'>
              {action.title}
            </td>
            <td className='px-3.5 py-2 text-slate-500 dark:text-slate-400 capitalize'>
              {isPhone ? 'Phone' : 'URL'}
            </td>
            <td className='px-3.5 py-2 text-slate-400 dark:text-slate-500 font-mono text-[11px] break-all'>
              {isPhone ? action.phone : action.url}
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const MediaPreview: React.FC<{ media: string[] }> = ({ media }) => (
  <div className='flex gap-2 flex-wrap'>
    {media.map((url, idx) => {
      const isImage = /\.(jpe?g|png|gif|webp|svg)(\?|$)/i.test(url);
      return isImage ? (
        <img
          key={idx}
          src={url}
          alt={`Media ${idx + 1}`}
          className='max-h-40 rounded border border-slate-200 dark:border-darkmode-400 object-contain'
        />
      ) : (
        <a
          key={idx}
          href={url}
          target='_blank'
          rel='noopener noreferrer'
          className='flex items-center gap-1.5 text-xs text-blue-500 underline break-all'
        >
          <Lucide icon='Paperclip' className='w-3.5 h-3.5 shrink-0' />
          {url}
        </a>
      );
    })}
  </div>
);

const ViewTwilioTemplate = ({ sid }: ITwilioTemplate) => {
  const client = getClientId();
  const [templateData, setTemplateData] = useState<TwilioTemplateData | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resolvedAccountSid, setResolvedAccountSid] = useState<string | null>(
    null
  );

  const canViewCredentials =
    checkSuperOwner() || checkOwner() || checkUserAdmin();

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);
      setTemplateData(null);
      setResolvedAccountSid(null);
      setShowTemplateDialog(true);
      const response = await AxiosInstance.get('/twilio/view', {
        params: { client, sid },
      });

      const data = response.data.data;
      setResolvedAccountSid(data.accountSid || null);

      const types = data.types;
      const typeKey = Object.keys(types)[0];
      const t = types[typeKey];

      setTemplateData({
        type: typeKey,
        body: t.body,
        media: t.media ?? [],
        actions: t.actions ?? [],
      });
    } catch (error: any) {
      const responseData = error?.response?.data;
      const msg = responseData?.message || 'Cannot view template';
      setErrorMessage(msg);
      if (responseData?.data?.accountSid) {
        setResolvedAccountSid(responseData.data.accountSid);
      }
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const typeKey = templateData?.type?.toLowerCase() ?? '';
  const isQuickReply =
    typeKey.includes('quick-reply') || typeKey.includes('quick_reply');
  const isCallToAction =
    typeKey.includes('call-to-action') ||
    typeKey.includes('call_to_action') ||
    typeKey.includes('cta');

  return (
    <>
      <Tippy as='div' content='Preview Template'>
        <Button
          type='button'
          onClick={fetchTemplate}
          disabled={!sid}
          variant='plain'
        >
          {loading ? <Loading className='hidden' /> : <Lucide icon='Eye' />}
        </Button>
      </Tippy>

      {showTemplateDialog && !loading && (
        <Dialog
          size='xl'
          open={showTemplateDialog}
          onClose={() => setShowTemplateDialog(false)}
        >
          <Dialog.Panel className='p-4'>
            <Dialog.Title>Twilio WhatsApp Template</Dialog.Title>

            <Dialog.Description>
              {/* Credentials for privileged users */}
              {canViewCredentials && resolvedAccountSid && (
                <div className='rounded-lg border border-slate-200 dark:border-darkmode-400 overflow-hidden mb-3'>
                  <div className='px-3.5 py-2.5'>
                    <p className='text-[10px] uppercase tracking-widest text-slate-400 mb-1'>
                      Account SID
                    </p>
                    <p className='text-xs font-mono text-slate-700 dark:text-slate-200 break-all'>
                      {resolvedAccountSid}
                    </p>
                  </div>
                </div>
              )}

              {/* Error */}
              {errorMessage && (
                <div className='rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-3.5 py-3 mb-3'>
                  <p className='text-sm text-red-600 dark:text-red-400'>
                    {errorMessage}
                  </p>
                </div>
              )}

              {/* Template content */}
              {templateData && (
                <>
                  {/* Type label */}
                  <p className='text-[10px] uppercase tracking-widest text-slate-400 mb-2'>
                    Template type:{' '}
                    <span className='font-medium text-slate-500 normal-case tracking-normal'>
                      {templateData.type}
                    </span>
                  </p>

                  {/* Card */}
                  <div className='rounded-lg border border-slate-200 dark:border-darkmode-400 overflow-hidden'>
                    {/* Media */}
                    {!!templateData.media?.length && (
                      <div className='px-3.5 py-3 border-b border-slate-200 dark:border-darkmode-400'>
                        <p className='text-[10px] uppercase tracking-widest text-slate-400 mb-2'>
                          Media
                        </p>
                        <MediaPreview media={templateData.media} />
                      </div>
                    )}

                    {/* Body */}
                    {templateData.body && (
                      <div className='px-3.5 py-3'>
                        <p className='text-[10px] uppercase tracking-widest text-slate-400 mb-1.5'>
                          Message
                        </p>
                        <p className='text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed'>
                          {templateData.body}
                        </p>
                      </div>
                    )}

                    {/* Quick replies */}
                    {isQuickReply && !!templateData.actions?.length && (
                      <Section label='Quick Replies' noPad>
                        <QuickReplies
                          actions={
                            templateData.actions as TwilioQuickReplyAction[]
                          }
                        />
                      </Section>
                    )}

                    {/* CTA */}
                    {isCallToAction && !!templateData.actions?.length && (
                      <Section label='Call to Action' noPad>
                        <CtaButtons
                          actions={templateData.actions as TwilioCtaAction[]}
                        />
                      </Section>
                    )}
                  </div>
                </>
              )}
            </Dialog.Description>
            <Dialog.Footer className='flex items-center justify-center'>
              <Button onClick={() => setShowTemplateDialog(false)}>
                Close
              </Button>
            </Dialog.Footer>
          </Dialog.Panel>
        </Dialog>
      )}
    </>
  );
};

export default ViewTwilioTemplate;
